import React, { useState, useEffect } from "react";
import { UserSummary, ParticipantSummary } from "../../gql/types";
import PlaydateInviteGuestListView from "../playdate/view/guest/PlaydateInviteGuestListView";
import { useLayout } from "../layout/LayoutContext";
import { OVERLAY_ZINDEX_CONSTANT } from "../layout/Layout";
import noUser from "../../assets/no-user.png";
import styles from "./ParticipantRowWithOthers.module.css";
import { truncateContent } from "utils/ContentTruncationComputer";

interface ParticipantRowWithOthersProps {
    mainParticipant: ParticipantSummary;
    otherParticipants?: ParticipantSummary[]; // Still restricted to UserSummary for guest list view
    className?: string;
    style?: React.CSSProperties;
    prefixText?: string;
}

export const ParticipantRowWithOthers: React.FC<ParticipantRowWithOthersProps> = ({
    mainParticipant,
    otherParticipants = [],
    className,
    style,
    prefixText = "+",
}) => {
    const TRUNCATION_LENGTH = 15;
    const SMALLER_LENGTH = 13;

    const [showOthers, setShowOthers] = useState(false);
    const { baseZIndex, showOverlay, hideOverlay } = useLayout();

    useEffect(() => {
        if (showOthers) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [showOthers]);

    const handleOpen = () => {
        showOverlay(true);
        setShowOthers(true);
    };

    const handleClose = () => {
        hideOverlay();
        setShowOthers(false);
    };

    const otherGuestsWithoutMain = otherParticipants.filter(
        (p) => p.id !== mainParticipant.id
    );

    const guestListZIndex = baseZIndex + OVERLAY_ZINDEX_CONSTANT;

    const isUser = (p: ParticipantSummary): p is UserSummary => "email" in p;

    const displayName = isUser(mainParticipant)
        ? `${mainParticipant.firstName || ""} ${mainParticipant.lastName || ""}`.trim()
        : mainParticipant.name;

    const profilePictureUrl = isUser(mainParticipant)
        ? mainParticipant.profilePictureUrl
        : mainParticipant.parents?.[0]?.profilePictureUrl;

    const altText = isUser(mainParticipant)
        ? mainParticipant.email
        : mainParticipant.name;

    const fontStyle = displayName.length > SMALLER_LENGTH
        ? styles.smallFont
        : styles.regularFont;

    const truncatedName = truncateContent(displayName, TRUNCATION_LENGTH);

    return (
        <>
            <div
                className={`${styles.rowContainer} ${className || ""}`}
                onClick={otherParticipants.length > 0 ? handleOpen : undefined}
                style={style}
            >
                <div className={styles.userSummary}>
                    <img
                        src={profilePictureUrl || noUser}
                        alt={altText}
                        className={styles.avatar}
                    />
                    <span className={`${styles.userName} ${fontStyle}`}>{truncatedName}</span>
                </div>

                {otherGuestsWithoutMain.length > 0 && (
                    <span className={`${styles.othersLabel} ${fontStyle}`}>
                        {prefixText} {otherGuestsWithoutMain.length} more
                    </span>
                )}
            </div>

            {showOthers && (
                <PlaydateInviteGuestListView
                    onBack={handleClose}
                    guests={otherParticipants}
                    isPortal={true}
                    baseZIndex={guestListZIndex}
                />
            )}
        </>
    );
};
